import { __assign, __rest, __spreadArray } from "tslib";
import classnames from 'classnames';
import React, { useCallback } from 'react';
import s from '../PasswordInput.scss';
import { Loader, Box, FloatingHelper, Text } from '@wix/design-system';
import { CircleSmallFilledSmall, StatusCompleteFilledSmall, InfoCircle, } from '@wix/wix-ui-icons-common';
import { PasswordStrengthExtractor, } from '../../../passwordStrength';
import { useTranslation } from 'react-i18next';
export function withPasswordStrength(WrappedComponent) {
    // Try to create a nice displayName for React Dev Tools.
    var displayName = WrappedComponent.displayName || WrappedComponent.name || 'Component';
    // Creating the inner component. The calculated Props type here is the where the magic happens.
    var InputWithPasswordStrength = function (_a, ref) {
        var inputValue = _a.value, props = __rest(_a, ["value"]);
        var value = inputValue;
        var passwordStrengthExtractor = PasswordStrengthExtractor.getInstance().withPolicy(props.passwordPolicy);
        var t = useTranslation().t;
        var _b = React.useState(false), visible = _b[0], setVisible = _b[1];
        var _c = React.useState(value), passwordValue = _c[0], setPasswordValue = _c[1];
        var _d = React.useState({}), injectedProps = _d[0], setInjectedProps = _d[1];
        var _e = React.useState(false), isDirty = _e[0], setDirty = _e[1];
        var inputRef = React.useRef(null);
        React.useEffect(function () {
            setPasswordValue(value);
        }, [value]);
        var _f = React.useState(passwordStrengthExtractor.getPasswordStrength(passwordValue)), passwordStrength = _f[0], setPasswordStrength = _f[1];
        var onFocus = React.useCallback(function (e) {
            var _a;
            setVisible(true);
            return (_a = props === null || props === void 0 ? void 0 : props.onFocus) === null || _a === void 0 ? void 0 : _a.call(props, e);
        }, [props === null || props === void 0 ? void 0 : props.onFocus]);
        var onBlur = React.useCallback(function (e) {
            var _a;
            setVisible(false);
            setDirty(true);
            if ((hints === null || hints === void 0 ? void 0 : hints.length) > 0) {
                setErrosIfNeeded(e.target.value, hints);
            }
            return (_a = props === null || props === void 0 ? void 0 : props.onBlur) === null || _a === void 0 ? void 0 : _a.call(props, e);
        }, [props === null || props === void 0 ? void 0 : props.onBlur, passwordValue, passwordStrength]);
        var onChange = React.useCallback(function (e) {
            var _a;
            setPasswordValue(e.target.value);
            var passwordStrength = passwordStrengthExtractor.getPasswordStrength(e.target.value);
            setPasswordStrength(passwordStrength);
            var hints = passwordStrength.hints;
            clearErrors();
            if (isDirty) {
                setErrosIfNeeded(e.target.value, hints);
            }
            return (_a = props === null || props === void 0 ? void 0 : props.onChange) === null || _a === void 0 ? void 0 : _a.call(props, e);
        }, [props === null || props === void 0 ? void 0 : props.onChange, isDirty, t, passwordValue]);
        var setError = useCallback(function (errorMsg) {
            var _a, _b;
            var message = errorMsg ? errorMsg : '';
            (_b = (_a = inputRef === null || inputRef === void 0 ? void 0 : inputRef.current) === null || _a === void 0 ? void 0 : _a.input) === null || _b === void 0 ? void 0 : _b.setCustomValidity(message);
            setInjectedProps({
                status: 'error',
                statusMessage: message,
            });
        }, [passwordValue, inputRef === null || inputRef === void 0 ? void 0 : inputRef.current]);
        var setErrosIfNeeded = useCallback(function (value, hints) {
            var _a, _b;
            if (value.length === 0) {
                return setError(t('password.policy.error.empty'));
            }
            if (props.passwordPolicy) {
                if (hints.length > 0) {
                    setError(t("password.policy.error.".concat(hints[0])));
                }
                return;
            }
            var minLength = (_a = props.minLength) !== null && _a !== void 0 ? _a : PasswordStrengthExtractor.MINIMAL_LENGTH;
            var maxLength = (_b = props.maxLength) !== null && _b !== void 0 ? _b : PasswordStrengthExtractor.MAXIMAL_LENGTH;
            var isTooShort = value.length < minLength;
            var isTooLong = value.length > maxLength;
            if (isTooShort) {
                return setError(t('password.policy.error.too_short', {
                    minLength: minLength,
                }));
            }
            if (isTooLong) {
                return setError(t('password.policy.error.too_long', {
                    maxLength: maxLength,
                }));
            }
        }, [passwordValue, inputRef === null || inputRef === void 0 ? void 0 : inputRef.current, setError, t]);
        var clearErrors = React.useCallback(function () {
            var _a, _b;
            (_b = (_a = inputRef === null || inputRef === void 0 ? void 0 : inputRef.current) === null || _a === void 0 ? void 0 : _a.input) === null || _b === void 0 ? void 0 : _b.setCustomValidity('');
            setInjectedProps({
                status: undefined,
                statusMessage: undefined,
            });
        }, [passwordValue, inputRef === null || inputRef === void 0 ? void 0 : inputRef.current]);
        React.useImperativeHandle(ref, function () {
            var _a, _b, _c;
            return {
                isValid: (_b = (_a = inputRef === null || inputRef === void 0 ? void 0 : inputRef.current) === null || _a === void 0 ? void 0 : _a.input) === null || _b === void 0 ? void 0 : _b.validity.valid,
                input: (_c = inputRef === null || inputRef === void 0 ? void 0 : inputRef.current) === null || _c === void 0 ? void 0 : _c.input,
                validationMessage: function () { var _a, _b; return (_b = (_a = inputRef === null || inputRef === void 0 ? void 0 : inputRef.current) === null || _a === void 0 ? void 0 : _a.input) === null || _b === void 0 ? void 0 : _b.validationMessage; },
                setError: setError,
            };
        });
        var _calasification = passwordStrength.classification, hints = passwordStrength.hints, finalScore = passwordStrength.finalScore;
        var classification = fromRawClassification({
            password: passwordValue,
            classification: _calasification,
        });
        var strengthSuffix = (React.createElement(Box, { key: "password-strength", verticalAlign: "middle", marginRight: props.suffix ? 1 : 0, marginBottom: "2px" },
            React.createElement(Text, { size: "tiny", secondary: true }, t(getPasswordStrengthTranslationKey({
                password: passwordValue,
                classification: _calasification,
            })))));
        var loader = (React.createElement(Box, { paddingLeft: 1, align: "center", verticalAlign: "middle", key: "loader", dataHook: "password-input-loader" },
            React.createElement(Loader, { size: "tiny" })));
        var suffix = __spreadArray(__spreadArray(__spreadArray([], (passwordValue &&
            props.showStrength &&
            !props.passwordPolicy &&
            !visible
            ? [strengthSuffix]
            : []), true), (props.suffix ? props.suffix : []), true), (props.showLoader ? [loader] : []), true);
        // props comes afterwards so the can override the default ones.
        var component = (React.createElement(WrappedComponent, __assign({}, injectedProps, props, { suffix: suffix, onFocus: onFocus, onBlur: onBlur, onChange: onChange, required: true, ref: inputRef })));
        return props.showStrength ? (props.isMobile ? (React.createElement(Box, { direction: "vertical" },
            component,
            visible && (React.createElement(Box, null,
                React.createElement(PasswordStrengthBody, { finalScore: finalScore, classification: classification, hints: hints, password: passwordValue, isMobile: props.isMobile, passwordPolicy: props.passwordPolicy }))))) : (React.createElement(Box, { direction: "vertical", width: "100%" },
            React.createElement(FloatingHelper, { width: "270px", opened: visible, appearance: "light", target: component, placement: "left", zIndex: 9999, content: React.createElement(FloatingHelper.Content, { body: React.createElement(PasswordStrengthBody, { finalScore: finalScore, classification: classification, hints: hints, password: passwordValue, passwordPolicy: props.passwordPolicy }) }) })))) : (component);
    };
    InputWithPasswordStrength.displayName = "withPasswordStrength(".concat(displayName, ")");
    return React.forwardRef(InputWithPasswordStrength);
}
var PasswordStrengthBody = function (_a) {
    var _b, _c, _d, _e, _f, _g;
    var finalScore = _a.finalScore, classification = _a.classification, hints = _a.hints, password = _a.password, passwordPolicy = _a.passwordPolicy, isMobile = _a.isMobile;
    var t = useTranslation().t;
    var withPolicy = !!passwordPolicy;
    // `hints` includes the password properties the current password miss so in
    // order to figure what the password includes we check what is missing
    var includeLetters = !hints.includes('letters');
    var includeSymbols = !hints.includes('symbol');
    var includeMoreCharacters = !hints.includes('more_characters');
    var includeNumbers = !hints.includes('numbers');
    return (React.createElement(Box, { direction: "vertical", width: "100%", gap: 2, textAlign: "left", marginTop: isMobile ? 3 : undefined },
        React.createElement(Box, { direction: isMobile ? 'horizontal' : 'vertical', align: "space-between" },
            React.createElement(Text, { size: "small", secondary: true, ariaLive: "polite", ariaAtomic: "true", weight: isMobile ? 'bold' : undefined }, t(getTitleTranslationKey({
                password: password,
                classification: classification,
                withPolicy: withPolicy,
            }))),
            !withPolicy && !isMobile && (React.createElement(Box, { className: classnames([
                    s.strengthIndicatorContainer,
                    (_b = {}, _b[s["level-".concat(classification)]] = (classification === null || classification === void 0 ? void 0 : classification.length) > 0, _b),
                    (_c = {}, _c[s.mobilePasswordStrength] = isMobile, _c),
                ]), direction: "horizontal", paddingTop: 1, paddingBottom: 1 },
                React.createElement(Box, { className: classnames([s.bar, (_d = {}, _d[s.isOn] = finalScore >= 0, _d)]) }),
                React.createElement(Box, { className: classnames([s.bar, (_e = {}, _e[s.isOn] = finalScore > 1, _e)]) }),
                React.createElement(Box, { className: classnames([s.bar, (_f = {}, _f[s.isOn] = finalScore > 2, _f)]) }),
                React.createElement(Box, { className: classnames([s.bar, (_g = {}, _g[s.isOn] = finalScore > 4, _g)]) })))),
        React.createElement(Box, { "aria-live": "polite", direction: "vertical", gap: 1 },
            withPolicy && (React.createElement(HintsWithPolicy, { passwordPolicy: passwordPolicy, includeLetters: includeLetters, includeSymbols: includeSymbols, includeMoreCharacters: includeMoreCharacters, includeNumbers: includeNumbers })),
            !withPolicy && (React.createElement(HintsWithoutPolicy, { includeLetters: includeLetters, includeSymbols: includeSymbols, includeMoreCharacters: includeMoreCharacters })))));
};
var Hint = function (_a) {
    var predicate = _a.predicate, children = _a.children;
    return (React.createElement(Box, null,
        React.createElement(Box, { color: "B05", marginRight: 1 }, predicate ? React.createElement(StatusCompleteFilledSmall, null) : React.createElement(CircleSmallFilledSmall, null)),
        React.createElement(Text, { size: "small", skin: predicate ? 'disabled' : undefined, className: predicate ? s.checked : undefined }, children)));
};
var HintsWithPolicy = function (_a) {
    var _b;
    var passwordPolicy = _a.passwordPolicy, includeLetters = _a.includeLetters, includeSymbols = _a.includeSymbols, includeMoreCharacters = _a.includeMoreCharacters, includeNumbers = _a.includeNumbers;
    var t = useTranslation().t;
    return (React.createElement(React.Fragment, null,
        React.createElement(Hint, { predicate: includeLetters }, t('password_tooltip_strength_details_letters')),
        React.createElement(Hint, { predicate: includeSymbols }, t('password_tooltip_strength_details_symbol')),
        React.createElement(Hint, { predicate: includeNumbers }, t('password_tooltip_strength_details_numbers')),
        React.createElement(Hint, { predicate: includeMoreCharacters }, t('password_tooltip_strength_details_minimal_length', {
            minimalLength: (_b = passwordPolicy === null || passwordPolicy === void 0 ? void 0 : passwordPolicy.minLength) !== null && _b !== void 0 ? _b : PasswordStrengthExtractor.MINIMAL_LENGTH,
        })),
        React.createElement(Box, { marginTop: 1, marginBottom: 3 },
            React.createElement(Box, { marginRight: 1 },
                React.createElement(InfoCircle, { width: "18px" })),
            React.createElement(Text, { size: "tiny" }, t('password_tooltip_strength_details_avoid_common_and_used')))));
};
var HintsWithoutPolicy = function (_a) {
    var includeLetters = _a.includeLetters, includeSymbols = _a.includeSymbols, includeMoreCharacters = _a.includeMoreCharacters;
    var t = useTranslation().t;
    return (React.createElement(React.Fragment, null,
        React.createElement(Text, { size: "small", secondary: true }, t('password_tooltip_strength_details_title')),
        React.createElement(Hint, { predicate: includeLetters }, t('password_tooltip_strength_details_letters')),
        React.createElement(Hint, { predicate: includeSymbols }, t('password_tooltip_strength_details_symbol')),
        React.createElement(Hint, { predicate: includeMoreCharacters }, t('password_tooltip_strength_details_more_characters'))));
};
var fromRawClassification = function (_a) {
    var password = _a.password, classification = _a.classification;
    return classification &&
        password &&
        password.length >= PasswordStrengthExtractor.MINIMAL_LENGTH
        ? classification
        : '';
};
var getTitleTranslationKey = function (_a) {
    var password = _a.password, classification = _a.classification, withPolicy = _a.withPolicy;
    if (withPolicy) {
        return 'password_tooltip_strength_bar_title';
    }
    return classification &&
        (password === null || password === void 0 ? void 0 : password.length) >= PasswordStrengthExtractor.MINIMAL_LENGTH
        ? "password_tooltip_strength_bar_level_".concat(classification)
        : 'password_tooltip_strength_bar_too_short';
};
var getPasswordStrengthTranslationKey = function (_a) {
    var password = _a.password, classification = _a.classification;
    return classification && password
        ? "password_tooltip_strength_level_".concat(classification)
        : '';
};
//# sourceMappingURL=WithPasswordStrength.js.map